import { Botao } from '../../../components/Botao'
import { IconeAlerta, IconeCheque } from '../../../components/Icones'
import { Cartao } from '../../../components/ListaDeDados'
import { useSessao } from '../../../auth/contexto'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import { ResumoDaClassificacao } from './ResumoDaClassificacao'
import { ResumoDoPlano } from './ResumoDoPlano'
import { ETAPA_DA_CLASSIFICACAO, ETAPA_DO_PLANO, etapaDoCampo, type Etapa } from './etapas'
import { pendenciasParaOComite } from './pendencias'
import type { FormularioNovaGmud } from './esquema'

interface Props {
  valores: FormularioNovaGmud
  aoCorrigir: (etapa: Etapa) => void
}

/** Volta para a etapa que contém o campo, com o rótulo dizendo para onde se vai. */
function BotaoCorrigir({ etapa, aoCorrigir }: { etapa: Etapa; aoCorrigir: (etapa: Etapa) => void }) {
  return (
    <Botao aparencia="texto" tamanho="pequeno" onClick={() => aoCorrigir(etapa)}>
      Corrigir {etapa.rotulo.toLowerCase()}
    </Botao>
  )
}

/**
 * Etapa 3 — conferir e salvar.
 *
 * Duas coisas que o sistema anterior não fazia e custaram caro. A primeira: dizer o que falta
 * ANTES de salvar, com o nome que os campos têm na tela. A segunda, e a mais importante: separar
 * salvar de enviar ao comitê. Lá, o cadastro terminava numa mensagem de sucesso, e a pessoa ia
 * embora achando que a mudança estava com o comitê — que nunca a recebeu. Pelo TR (ANEXO XXI),
 * nada entra em produção sem decisão do comitê; uma solicitação esquecida em rascunho é uma
 * implantação que vai acontecer sem aprovação ou não vai acontecer.
 *
 * @param props o que foi preenchido e como voltar para uma etapa
 * @returns o conteúdo da terceira etapa
 */
export function EtapaConferencia({ valores, aoCorrigir }: Props) {
  const { usuario } = useSessao()
  // Quem só executa preenche tudo e descobre no fim que 'Seu perfil não permite' enviar: dizer antes.
  const podeEnviar = (usuario?.papeis ?? []).some((papel) => papel === 'GMUD_SOLICITANTE' || papel === 'ADMIN')
  const pendencias = pendenciasParaOComite(valores)

  return (
    <div className="space-y-5">
      <Cartao
        titulo="Classificação"
        acao={<BotaoCorrigir etapa={ETAPA_DA_CLASSIFICACAO} aoCorrigir={aoCorrigir} />}
      >
        <ResumoDaClassificacao valores={valores} />
      </Cartao>

      <Cartao titulo="Plano da mudança" acao={<BotaoCorrigir etapa={ETAPA_DO_PLANO} aoCorrigir={aoCorrigir} />}>
        <ResumoDoPlano valores={valores} />
      </Cartao>

      {pendencias.length > 0 ? (
        <section className="rounded-xl border border-laranja-200 bg-laranja-50 p-4 text-sm text-laranja-900">
          <h2 className="flex items-center gap-2 font-semibold">
            <IconeAlerta className="h-4 w-4" />
            Falta preencher para enviar ao {TERMOS.cab.rotulo.toLowerCase()}
          </h2>
          <ul className="mt-2 list-inside list-disc space-y-1">
            {pendencias.map((pendencia) => (
              <li key={pendencia.campo}>
                {pendencia.rotulo}
                <span className="text-laranja-800"> — etapa {etapaDoCampo(pendencia.campo)?.numero}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3">
            Você pode salvar assim mesmo e continuar depois. Esta lista é a mesma que o sistema vai cobrar no
            momento do envio.
          </p>
        </section>
      ) : (
        <section className="flex gap-2 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-900">
          <IconeCheque className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {podeEnviar
              ? TEXTOS.conferenciaDoCadastro.planoCompleto(TERMOS.cab.rotulo.toLowerCase())
              : TEXTOS.conferenciaDoCadastro.envioDeOutro}
          </p>
        </section>
      )}

      <section className="cartao p-4 text-sm text-slate-700">
        <h2 className="text-base font-semibold text-slate-900">Salvar não envia ao {TERMOS.cab.rotulo.toLowerCase()}</h2>
        <p className="mt-1">
          A solicitação é gravada como rascunho e fica visível só para você. O envio ao comitê é uma ação separada,
          feita na tela da solicitação quando o plano estiver pronto — e é o comitê que autoriza a implantação.
        </p>
      </section>
    </div>
  )
}
